import { useCallback, useEffect, useState } from 'react';
import { fetchAPI, postAPI } from '../../../utils/utils';
import { postOrderEdit } from './orderEdits';
import { can } from '../../../utils/permissions';
import moment from 'moment';
import Pressable from '../../other/Pressable';
import styles from './Prova.styles';
import { ModelFolderTrash } from './ModelFolderBrowser';

function PaperBin({ event, bundlesInfo, socket, setProves, setOrder, order, userInfo, modelFolders }) {
    const [deletedProves, setDeletedProves] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const canManageDeletedProves = can(userInfo, 'MANAGE_DELETED_PROVES');

    const loadDeletedProves = useCallback(() => {
        setIsLoading(true);
        fetchAPI(`/deleted_proves/${event}`, (res) => {
            if (res.success) {
                setDeletedProves(res.deleted_proves || []);
            } else {
                console.error('Error loading deleted proves:', res.error);
            }
            setIsLoading(false);
        }, false);
    }, [event]);

    useEffect(() => {
        if (!isOpen || !canManageDeletedProves) return;

        loadDeletedProves();
        const onProvaChanged = eventId => {
            if (String(eventId) === String(event)) loadDeletedProves();
        };
        socket.on('.deleted_prova', onProvaChanged);
        socket.on('.restored_prova', onProvaChanged);
        socket.on('.model_folders_updated', onProvaChanged);
        socket.on('connect', loadDeletedProves);

        return () => {
            socket.off('.deleted_prova', onProvaChanged);
            socket.off('.restored_prova', onProvaChanged);
            socket.off('.model_folders_updated', onProvaChanged);
            socket.off('connect', loadDeletedProves);
        };
    }, [isOpen, event, canManageDeletedProves, socket, loadDeletedProves]);

    const handleRestore = (castell, versio, destination = 'public') => {
        if (!canManageDeletedProves) return;
        
        postAPI('/restore_prova', { event, castell, versio, destination, userId: userInfo?.id }, (res) => {
            if (res.success) {
                // Update the local state to remove the restored prova
                setDeletedProves(prev => prev.filter(p => !(p.castell === castell && p.versio === versio)));
                
                // Fetch updated proves list
                fetchAPI(`/proves/${event}`, res => setProves(res));
                
                // Add the restored prova to the order at the end
                const fullName = `${castell}.${versio}.canvis`;
                if (modelFolders) modelFolders.refresh();
                if (!modelFolders) {
                    postOrderEdit({
                        event, edit: { op: 'append', proves: [fullName] }, socket, userId: userInfo?.id,
                        onOrder: next => { if (next) setOrder(next); },
                        // Servidors d'abans de /order_edit
                        onUnsupported: () => {
                            if (order.includes(fullName)) return;
                            const newOrder = [...order, fullName];
                            setOrder(newOrder);
                            socket.emit('.change_order', event, newOrder, userInfo?.id || null);
                        },
                        onFailure: () => fetchAPI(`/order/${event}`, res => setOrder(res), false),
                    });
                }
            } else {
                alert(`Error restaurant la prova! ${res?.error}`);
            }
        });
    };

    const getCastellName = (castellId) => {
        const bundle = bundlesInfo.find(b => b.id === castellId);
        return bundle ? (bundle.shortName || bundle.nom) : castellId;
    };

    if (!canManageDeletedProves) return null;

    return (
        <div style={{ marginTop: 10 }}>
            <Pressable
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 15px',
                    backgroundColor: '#f0f4f8',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    border: '1px solid #e9ecef'
                }} 
                onClick={() => setIsOpen(!isOpen)}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '10px', fontSize: '16px', opacity: 0.7 }}>{modelFolders ? '🗃️' : '🗑️'}</span>
                    <span style={{ fontSize: '15px', color: '#333' }}>{modelFolders ? 'Arxivats de Pinyes Model' : 'Paperera de proves eliminades'}</span>
                </div>
                <span style={{ fontSize: '10px', color: '#666' }}>
                    {isOpen ? '▲' : '▼'}
                </span>
            </Pressable>

            {isOpen && (
                <div style={{ 
                    backgroundColor: '#f8f9fa',
                    borderRadius: '0 0 5px 5px',
                    padding: '15px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: '1px'
                }}>
                    {modelFolders && <ModelFolderTrash models={modelFolders} bundlesInfo={bundlesInfo} userInfo={userInfo}
                        legacyProves={deletedProves} onRestoreLegacy={handleRestore} onDeletedLegacy={loadDeletedProves} />}
                    {isLoading ? (
                        <div style={{ textAlign: 'center', padding: '20px' }}>Carregant...</div>
                    ) : deletedProves.length === 0 ? (modelFolders?.data?.trash?.length ? null : (
                        <div style={{ 
                            textAlign: 'center', 
                            padding: '30px', 
                            color: '#888', 
                            fontStyle: 'italic',
                            width: '100%'
                        }}>
                            {modelFolders ? 'No hi ha pinyes ni carpetes arxivades' : 'No hi ha proves eliminades'}
                        </div>
                    )) : modelFolders ? null : (
                        deletedProves.map((prova, index) => (
                            <div 
                                key={`${prova.castell}.${prova.versio}`}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '12px 15px',
                                    marginBottom: '8px',
                                    backgroundColor: 'white',
                                    width: '100%',
                                    borderRadius: '5px',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                    border: '1px solid #e9ecef'
                                }}
                            >
                                <div>
                                    <div style={{ fontWeight: 'bold', color: '#2859A8', fontSize: '15px' }}>
                                        {getCastellName(prova.castell)}
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#888', marginTop: '3px' }}>
                                        Eliminat fa {moment(prova.deletedAt).fromNow(true)}
                                    </div>
                                </div>
                                <Pressable
                                    style={{
                                        backgroundColor: '#4A90E2',
                                        color: 'white',
                                        padding: '6px 16px',
                                        borderRadius: '4px',
                                        border: 'none',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onClick={() => handleRestore(prova.castell, prova.versio, 'public')}
                                >
                                    Restaurar
                                </Pressable>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

export default PaperBin;
